import React, { useState } from 'react';
import { fetchWithAuth } from '../../utils/api';

function DatabaseManagement({ user, handleLogout }) {
  const [isRestoring, setIsRestoring] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  // Two-step confirmation for restore. Selecting a file opens a modal
  // that requires the admin to type RESTORE before the import button
  // enables. A single accidental click can't wipe data anymore.
  const [pendingRestore, setPendingRestore] = useState(null);
  const [confirmText, setConfirmText] = useState('');

  const handleExportJSON = () => {
    fetchWithAuth('/export', {}, user, handleLogout)
      .then(async res => {
        if (!res.ok) throw new Error(await res.text());
        return res.blob();
      })
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `farm_admin_backup_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(err => setMessage({ text: "Backup failed: " + err.message, type: 'error' }));
  };

  const handleExportDB = () => {
    fetchWithAuth('/export/db', {}, user, handleLogout)
      .then(async res => {
        if (!res.ok) throw new Error(await res.text());
        return res.blob();
      })
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `noknok_${new Date().toISOString().split('T')[0]}.db`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(err => setMessage({ text: "DB download failed: " + err.message, type: 'error' }));
  };

  // Step 1: file picked → stash it, open confirmation modal.
  const handleFileSelected = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPendingRestore(file);
    setConfirmText('');
    e.target.value = '';
  };

  const cancelRestore = () => {
    setPendingRestore(null);
    setConfirmText('');
  };

  // Step 2: confirmation typed → run the actual upload.
  const performRestore = async () => {
    if (!pendingRestore || confirmText.trim() !== 'RESTORE') return;

    const file = pendingRestore;
    setPendingRestore(null);
    setConfirmText('');
    setIsRestoring(true);
    setMessage({ text: 'Restoring data... Please wait.', type: 'info' });

    const formData = new FormData();
    formData.append('backup', file);

    try {
      const res = await fetchWithAuth('/import', {
        method: 'POST',
        body: formData
      }, user, handleLogout);

      const text = await res.text();
      if (res.ok) {
        setMessage({ text: text + ' Redirecting to login...', type: 'success' });
        setTimeout(() => window.location.reload(), 3000);
      } else {
        setMessage({ text: 'Error: ' + text, type: 'error' });
      }
    } catch (err) {
      setMessage({ text: 'Restoration failed: ' + err.message, type: 'error' });
    } finally {
      setIsRestoring(false);
    }
  };

  return (
    <div className="database-management-view">
      <div className="view-header">
        <h2>System Backup & Restore</h2>
      </div>

      <div className="view-intro">
        <p>Manage your farm data by exporting current records or restoring from a previous backup.</p>
        <p className="warning-text"><strong>Warning:</strong> Restoring data will replace all current information. A timestamped server-side snapshot is taken automatically before the restore runs so the previous state can be recovered from disk.</p>
      </div>

      {message.text && (
        <div className={`status-message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="backup-actions">
        <button className="submit-btn" onClick={handleExportJSON}>
          Export as JSON
        </button>
        <button className="secondary-btn" onClick={handleExportDB}>
          Download SQLite (.db)
        </button>
      </div>

      <div className="restore-panel">
        <h4>Restore from Backup</h4>
        <p className="restore-hint">Upload a previously exported <strong>.json</strong> or <strong>.db</strong> file.</p>
        <div className="file-upload-wrapper">
          <input
            type="file"
            accept=".json,.db"
            onChange={handleFileSelected}
            disabled={isRestoring}
            className="file-input"
          />
        </div>
        {isRestoring && <div className="processing-loader">Processing restoration...</div>}
      </div>

      {pendingRestore && (
        <div className="modal" onClick={cancelRestore}>
          <div
            className="modal-content"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '500px' }}
          >
            <div className="modal-header">
              <h3 style={{ margin: 0, color: 'var(--google-red)' }}>Confirm Restore</h3>
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{ marginBottom: '0.75rem' }}>
                You are about to overwrite the current database with:
              </p>
              <p style={{
                padding: '0.5rem 0.75rem',
                background: 'var(--google-grey-100, #f1f3f4)',
                borderRadius: 'var(--border-radius-sm)',
                fontFamily: 'monospace',
                wordBreak: 'break-all',
              }}>
                {pendingRestore.name} <span style={{ color: 'var(--google-grey-600)', fontSize: '0.85rem' }}>({(pendingRestore.size / 1024).toFixed(1)} KB)</span>
              </p>
              <p style={{ marginTop: '0.75rem', fontSize: '0.9rem', color: 'var(--google-grey-700, #5f6368)' }}>
                The server will save a timestamped snapshot of the current database before applying this upload, so the previous state can be recovered from disk.
              </p>
              <p style={{ marginTop: '1rem', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                Type <strong>RESTORE</strong> below to confirm:
              </p>
              <input
                type="text"
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                placeholder="RESTORE"
                autoFocus
                style={{ width: '100%' }}
              />
            </div>
            <div className="form-actions">
              <button className="secondary-btn" onClick={cancelRestore}>Cancel</button>
              <button
                className="cancel-btn"
                onClick={performRestore}
                disabled={confirmText.trim() !== 'RESTORE'}
              >
                Restore Database
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DatabaseManagement;
